// @ts-check

/**
 * BECK-722 mobile placement fallback.
 *
 * Spec: on tagged page types (data-beck-page-type set on <body>), the newsletter
 * teaser should appear at the bottom of the page on mobile when no editor anchor
 * is present. CSS handles the case where an anchor exists (hides the LB-placed
 * copy, shows the anchor copy). This script handles the no-anchor case for page
 * types whose desktop position is NOT already at the bottom of the page —
 * ressorts, tags, legal_areas, legal_areas_glossary. The LB-placed teaser is
 * physically relocated to just before .Template-footer below the breakpoint,
 * and restored to its original DOM position above it.
 *
 * Untagged pages (article, search, basic, exam) are left alone — their natural
 * placement is already correct on both viewports.
 *
 * This is a workaround until backend backfills the mobile anchor on existing
 * pages via an update hook. Once that lands, this script becomes a no-op
 * (anchor present → early return).
 */

const MOBILE_QUERY = "(width < 48rem)";
const EXCLUDED_PAGE_TYPES = new Set(["article", "search"]);

/**
 *
 */
export function initNewsletterMobileFallback() {
	const pageType = document.body.dataset.beckPageType;
	if (!pageType || EXCLUDED_PAGE_TYPES.has(pageType)) return;

	// Editor anchor present → CSS handles placement, JS bails.
	if (document.querySelector(".inxmail-subscribe-beck-aktuell-mobile-anchor")) {
		return;
	}

	const teaser = /** @type {HTMLElement | null} */ (
		document.querySelector(".TeaserHeadliner--newsletter")
	);
	if (!teaser) return;

	const footer = document.querySelector(".Template-footer");
	if (!footer?.parentElement) return;

	const originalParent = /** @type {HTMLElement | null} */ (
		teaser.parentElement
	);
	if (!originalParent) return;

	const originalNextSibling = teaser.nextSibling;
	const mobileContainer = footer.parentElement;
	const mq = window.matchMedia(MOBILE_QUERY);
	/** @type {HTMLElement | null} */
	let relocatedWrapper = null;

	/**
	 *
	 */
	function reposition() {
		if (mq.matches) {
			if (!relocatedWrapper) {
				relocatedWrapper = document.createElement("div");
				relocatedWrapper.className = "u-container";
				relocatedWrapper.dataset.beckRelocatedWrapper = "true";
			}
			relocatedWrapper.appendChild(teaser);
			mobileContainer.insertBefore(relocatedWrapper, footer);
			// Collapse the now-empty wrapper so it doesn't leave a visual gap
			// (the .Template-newsletterHeadliner top slot has 32px of padding).
			if (originalParent.children.length === 0) {
				originalParent.dataset.beckEmptyAfterRelocate = "true";
			}
		} else {
			delete originalParent.dataset.beckEmptyAfterRelocate;
			if (originalNextSibling) {
				originalParent.insertBefore(teaser, originalNextSibling);
			} else {
				originalParent.appendChild(teaser);
			}
			if (relocatedWrapper) {
				relocatedWrapper.remove();
				relocatedWrapper = null;
			}
		}
	}

	reposition();
	mq.addEventListener("change", reposition);
}

if (typeof document !== "undefined") {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initNewsletterMobileFallback);
	} else {
		initNewsletterMobileFallback();
	}
}

export default { initNewsletterMobileFallback };
